/**
 * @typedef {'Open'|'Fulfilled'|'Shipped'|'Completed'} PartOrderStatus
 * @typedef {'FSL'|'Warehouse'|'Supplier'|'OnSite'} FulfillmentSource
 * @typedef {'FSL'|'Warehouse'|'Supplier'|'OnSite'} FulfillmentMethod
 *
 * @typedef {Object} PartOrder
 * @property {string} id                  - GUID primary key
 * @property {string} ticketNumber        - display-only (e.g. "INC10041")
 * @property {string} partNumber
 * @property {string} partDescription
 * @property {string} customerName
 * @property {string|null} resellerName  - set when the order is managed by a reseller on behalf of an end-customer
 * @property {PartOrderStatus} status
 * @property {string} createdDate         - ISO date string
 * @property {string} slaDueDate          - ISO date string
 * @property {string} sla                - SLA tier name (e.g. "24x7x4 - Parts/TAC/FE")
 * @property {FulfillmentSource} source   - legacy, kept for compatibility
 * @property {FulfillmentMethod} fulfillmentMethod
 * @property {string|null} locationName   - specific stock location name (FSL/Warehouse)
 * @property {string|null} shipmentId               - GUID FK → shipments.id
 * @property {string|null} shipmentNumber           - display-only (e.g. "SHP-30011")
 * @property {string|null} linkedPurchaseOrderId    - GUID FK → purchaseOrders.id
 * @property {string|null} linkedPurchaseOrderNumber - display-only (e.g. "PO-6007")
 * @property {string|null} serialNumber             - serial number assigned at fulfillment (null for Supplier or non-serialized parts)
 */

/** @type {PartOrder[]} */
export const partOrders = [
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000001',
    ticketNumber: 'INC10041',
    partNumber: 'PWR-7200-AC',
    partDescription: 'AC Power Supply 720W',
    customerName: 'Harborview Medical Center',
    resellerName: 'Keystone IT Solutions',
    customerAddress: '2400 Medical Center Dr, Nashville, TN 37232',
    customerLat: 36.165, customerLon: -86.784, // Nashville, TN
    status: 'Completed',
    createdDate: '2026-03-10',
    slaDueDate: '2026-03-18',
    sla: '24x7x4 - Parts/TAC/FE',
    source: 'FSL',
    fulfillmentMethod: 'FSL',
    locationName: 'FSL Atlanta',
    shipmentId: 'b2c3d4e5-0002-4000-8002-200000000002',
    shipmentNumber: 'SHP-30011',
    serialNumber: 'SN-PWR-003',
    fulfillmentPhoto: {
      dataUrl: '/server-chassis.jpg',
      uploadedAt: '2026-03-16T10:24:00.000Z',
    },
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000002',
    ticketNumber: 'INC10052',
    partNumber: 'NIC-1G-SFP',
    partDescription: '1G SFP Fibre NIC',
    customerName: 'Coastal Capital Group',
    resellerName: 'Baybridge Tech Partners',
    customerAddress: '500 W Wisconsin Ave, Milwaukee, WI 53203',
    customerLat: 43.049, customerLon: -87.905, // Milwaukee, WI
    status: 'Shipped',
    createdDate: '2026-03-16',
    slaDueDate: '2026-03-24',
    sla: '9x5xNBD - Parts/TAC',
    source: 'Warehouse',
    fulfillmentMethod: 'Warehouse',
    locationName: 'Central Warehouse — Dallas',
    shipmentId: 'b2c3d4e5-0002-4000-8002-200000000003',
    shipmentNumber: 'SHP-30014',
    serialNumber: 'SN-NIC-034',
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000003',
    ticketNumber: 'INC10063',
    partNumber: 'RAM-32GB-DDR5',
    partDescription: '32GB DDR5 ECC DIMM',
    customerName: 'Ridgeline Data Centers',
    resellerName: 'IBM Logistics',
    customerAddress: '800 Congress Ave, Austin, TX 78701',
    customerLat: 30.267, customerLon: -97.743, // Austin, TX
    status: 'Awaiting Quote',
    createdDate: '2026-03-18',
    slaDueDate: '2026-03-26',
    sla: '24x7x4 - Parts/TAC',
    source: null,
    fulfillmentMethod: null,
    locationName: null,
    shipmentId: null,
    shipmentNumber: null,
    serialNumber: null,
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000004',
    ticketNumber: 'INC10071',
    partNumber: 'SSD-960-SATA',
    partDescription: '960GB SATA SSD 2.5"',
    customerName: 'Clearwater Regional Hospital',
    resellerName: 'Ironworks Manufacturing',
    customerAddress: '55 Industrial Blvd, Detroit, MI 48201',
    customerLat: 42.331, customerLon: -83.046, // Detroit, MI
    status: 'Fulfilled',
    createdDate: '2026-03-17',
    slaDueDate: '2026-03-25',
    sla: '24x7x4 - Parts',
    source: 'FSL',
    fulfillmentMethod: 'FSL',
    locationName: 'FSL Chicago',
    shipmentId: null,
    shipmentNumber: null,
    serialNumber: 'SN-SSD-040',
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000005',
    ticketNumber: 'INC10085',
    partNumber: 'FAN-CHASSIS-4U',
    partDescription: '4U Chassis Cooling Fan',
    customerName: 'Greenfield Hospital Trust',
    resellerName: 'Keystone IT Solutions',
    customerAddress: '1 Hospital Drive, Chicago, IL 60601',
    customerLat: 41.878, customerLon: -87.630, // Chicago, IL
    status: 'Shipped',
    createdDate: '2026-03-15',
    slaDueDate: '2026-03-23',
    sla: '24x7x4 - Parts/TAC',
    source: 'Warehouse',
    fulfillmentMethod: 'Warehouse',
    locationName: 'East Coast Warehouse',
    shipmentId: 'b2c3d4e5-0002-4000-8002-200000000004',
    shipmentNumber: 'SHP-30017',
    serialNumber: 'SN-FAN-001',
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000006',
    ticketNumber: 'INC10092',
    partNumber: 'HBA-16G-FC',
    partDescription: '16Gbps Fibre Channel HBA',
    customerName: 'Ashford Capital Partners',
    resellerName: 'Baybridge Tech Partners',
    customerAddress: '88 Wall St, New York, NY 10005',
    customerLat: 40.712, customerLon: -74.006, // New York, NY
    status: 'Shipped',
    createdDate: '2026-03-07',
    slaDueDate: '2026-03-15',
    sla: '9x5xNBD - Parts/TAC/FE',
    source: 'Supplier',
    fulfillmentMethod: 'Supplier',
    locationName: null,
    shipmentId: 'b2c3d4e5-0002-4000-8002-200000000001',
    shipmentNumber: 'SHP-30009',
    linkedPurchaseOrderId: 'g1a2b3c4-0021-4000-8021-210000000007',
    linkedPurchaseOrderNumber: 'PO-6007',
    serialNumber: 'SN-HBA-048',
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000007',
    ticketNumber: 'INC10104',
    partNumber: 'PWR-7200-AC',
    partDescription: 'AC Power Supply 720W',
    customerName: 'Westfield City Council',
    resellerName: 'Baybridge Tech Partners',
    customerAddress: '1 Municipal Dr, Clearfield, PA 16830',
    customerLat: 41.020, customerLon: -78.439, // Clearfield, PA
    status: 'Fulfilled',
    createdDate: '2026-03-19',
    slaDueDate: '2026-03-27',
    sla: '9x5xNBD - Parts',
    source: 'Warehouse',
    fulfillmentMethod: 'Warehouse',
    locationName: 'East Coast Warehouse',
    shipmentId: null,
    shipmentNumber: null,
    serialNumber: 'SN-PWR-015',
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000008',
    ticketNumber: 'INC10118',
    partNumber: 'FAN-CHASSIS-4U',
    partDescription: '4U Chassis Cooling Fan',
    customerName: 'Harborview Medical Center',
    resellerName: 'Keystone IT Solutions',
    customerAddress: '2400 Medical Center Dr, Nashville, TN 37232',
    customerLat: 36.165, customerLon: -86.784, // Nashville, TN
    status: 'Fulfilled',
    createdDate: '2026-03-21',
    slaDueDate: '2026-03-28',
    sla: '24x7x4 - Parts/TAC/FE',
    source: 'OnSite',
    fulfillmentMethod: 'OnSite',
    locationName: 'On-Site — Harborview Medical Center',
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: 'SN-FAN-002',
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000009',
    ticketNumber: 'INC10125',
    partNumber: 'NIC-10G-RJ45',
    partDescription: '10GbE RJ45 Network Card',
    customerName: 'BlueStar Technologies',
    resellerName: 'IBM Logistics',
    customerAddress: '800 Congress Ave, Austin, TX 78701',
    customerLat: 30.267, customerLon: -97.743, // Austin, TX
    status: 'Shipped',
    createdDate: '2026-03-22',
    slaDueDate: '2026-03-30',
    sla: '9x5xNBD - Parts/TAC',
    source: 'OnSite',
    fulfillmentMethod: 'OnSite',
    locationName: 'On-Site — IBM Logistics',
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: 'SN-N10G-001',
  },

  // ── Portal self-fulfillment test tickets ──────────────────────────────────
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000010',
    ticketNumber: 'INC10132',
    partNumber: 'PWR-7200-AC',
    partDescription: 'AC Power Supply 720W',
    customerName: 'Harborview Medical Center',
    resellerName: 'Keystone IT Solutions',
    customerAddress: '2400 Medical Center Dr, Nashville, TN 37232',
    customerLat: 36.165, customerLon: -86.784,
    status: 'Open',
    createdDate: '2026-03-28',
    slaDueDate: '2026-04-05',
    sla: '24x7x4 - Parts/TAC/FE',
    source: null,
    fulfillmentMethod: null,
    locationName: null,
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: null,
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000011',
    ticketNumber: 'INC10143',
    partNumber: 'FAN-CHASSIS-4U',
    partDescription: '4U Chassis Cooling Fan',
    customerName: 'Harborview Medical Center',
    resellerName: 'IBM Logistics',  // same end-customer, different reseller (edge case)
    customerAddress: '2400 Medical Center Dr, Nashville, TN 37232',
    customerLat: 36.165, customerLon: -86.784,
    status: 'Open',
    createdDate: '2026-03-30',
    slaDueDate: '2026-04-07',
    sla: '24x7x4 - Parts',
    source: null,
    fulfillmentMethod: null,
    locationName: null,
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: null,
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000012',
    ticketNumber: 'INC10154',
    partNumber: 'NIC-1G-SFP',
    partDescription: '1G SFP Fibre NIC',
    customerName: 'Coastal Capital Group',
    resellerName: 'Baybridge Tech Partners',
    customerAddress: '500 W Wisconsin Ave, Milwaukee, WI 53203',
    customerLat: 43.049, customerLon: -87.905,
    status: 'Open',
    createdDate: '2026-03-29',
    slaDueDate: '2026-04-06',
    sla: '9x5xNBD - Parts/TAC',
    source: null,
    fulfillmentMethod: null,
    locationName: null,
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: null,
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000013',
    ticketNumber: 'INC10165',
    partNumber: 'NIC-10G-RJ45',
    partDescription: '10GbE RJ45 Network Card',
    customerName: 'Ridgeline Data Centers',
    resellerName: 'IBM Logistics',
    customerAddress: '800 Congress Ave, Austin, TX 78701',
    customerLat: 30.267, customerLon: -97.743,
    status: 'Open',
    createdDate: '2026-04-01',
    slaDueDate: '2026-04-08',
    sla: '24x7x4 - Parts/TAC',
    source: null,
    fulfillmentMethod: null,
    locationName: null,
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: null,
  },

  // ── Keystone IT Solutions reseller orders ───────────────────────────────────
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000014',
    ticketNumber: 'INC10172',
    partNumber: 'SSD-3840-NVMe',
    partDescription: '3.84TB NVMe U.2 SSD',
    customerName: 'NovaStar Research Labs',
    resellerName: 'Keystone IT Solutions',
    customerAddress: '5000 Lakewood Ranch Blvd, Sarasota, FL 34211',
    customerLat: 27.384, customerLon: -82.456,
    status: 'Shipped',
    createdDate: '2026-03-25',
    slaDueDate: '2026-04-02',
    sla: '9x5xNBD - Parts/TAC/FE',
    source: 'Warehouse',
    fulfillmentMethod: 'Warehouse',
    locationName: 'East Coast Warehouse',
    shipmentId: 'b2c3d4e5-0002-4000-8002-200000000009',
    shipmentNumber: 'SHP-30028',
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: 'SN-N38-001',
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000015',
    ticketNumber: 'INC10181',
    partNumber: 'NIC-10G-RJ45',
    partDescription: '10GbE RJ45 Network Card',
    customerName: 'Greenfield Hospital Trust',
    resellerName: 'Keystone IT Solutions',
    customerAddress: '1 Hospital Drive, Chicago, IL 60601',
    customerLat: 41.878, customerLon: -87.630,
    status: 'Open',
    createdDate: '2026-03-31',
    slaDueDate: '2026-04-08',
    sla: '24x7x4 - Parts',
    source: null,
    fulfillmentMethod: null,
    locationName: null,
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: null,
  },
  {
    id: 'c1a2b3d4-0001-4000-8001-100000000016',
    ticketNumber: 'INC10188',
    partNumber: 'HBA-16G-FC',
    partDescription: '16Gbps Fibre Channel HBA',
    customerName: 'NovaStar Research Labs',
    resellerName: 'Keystone IT Solutions',
    customerAddress: '5000 Lakewood Ranch Blvd, Sarasota, FL 34211',
    customerLat: 27.384, customerLon: -82.456,
    status: 'Awaiting Quote',
    createdDate: '2026-04-02',
    slaDueDate: '2026-04-10',
    sla: '9x5xNBD - Parts/TAC',
    source: null,
    fulfillmentMethod: null,
    locationName: null,
    shipmentId: null,
    shipmentNumber: null,
    linkedPurchaseOrderId: null,
    linkedPurchaseOrderNumber: null,
    serialNumber: null,
  },
];

// ---------------------------------------------------------------------------

/**
 * @typedef {'Active'|'History'} PartReplenishmentOrderStatus
 *
 * @typedef {Object} PartReplenishmentOrder
 * @property {string} id                  - GUID primary key
 * @property {string} orderNumber         - display-only (e.g. "SPR-5001")
 * @property {string} vendorName
 * @property {string} stockLocation        - matches warehouses[].name
 * @property {string} partNumber
 * @property {string} partDescription
 * @property {PartReplenishmentOrderStatus} status
 * @property {string} createdDate         - ISO date string
 * @property {string|null} vendorShipmentId     - GUID FK → vendorShipments.id
 * @property {string|null} vendorShipmentNumber - display-only (e.g. "VSPH-8001")
 */

/** @type {PartReplenishmentOrder[]} */
export const partReplenishmentOrders = [
  {
    id: 'e4f5a6b7-0004-4000-8004-400000000001',
    orderNumber: 'REP5001',
    vendorName: 'Vantage Technology Supply',
    stockLocation: 'On-Site — IBM Logistics',
    partNumber: 'RAM-32GB-DDR5',
    partDescription: '32GB DDR5 ECC DIMM',
    status: 'Active',
    createdDate: '2026-03-20',
    vendorShipmentId: 'd3e4f5a6-0003-4000-8003-300000000001',
    vendorShipmentNumber: 'RS8001',
    linkedPurchaseOrderId: 'g1a2b3c4-0021-4000-8021-210000000001',
    linkedPurchaseOrderNumber: 'PO-6001',
  },
  {
    id: 'e4f5a6b7-0004-4000-8004-400000000002',
    orderNumber: 'REP5002',
    vendorName: 'Crestline Distribution',
    stockLocation: 'East Coast Warehouse',
    partNumber: 'HBA-16G-FC',
    partDescription: '16Gbps Fibre Channel HBA',
    status: 'History',
    createdDate: '2026-03-10',
    vendorShipmentId: 'd3e4f5a6-0003-4000-8003-300000000002',
    vendorShipmentNumber: 'RS8002',
    linkedPurchaseOrderId: 'g1a2b3c4-0021-4000-8021-210000000002',
    linkedPurchaseOrderNumber: 'PO-6002',
  },
  {
    id: 'e4f5a6b7-0004-4000-8004-400000000003',
    orderNumber: 'REP5003',
    vendorName: 'Bridgepoint Components',
    stockLocation: 'On-Site — Coastal Capital Group',
    partNumber: 'NIC-10G-RJ45',
    partDescription: '10GbE RJ45 Network Card',
    status: 'Active',
    createdDate: '2026-03-21',
    vendorShipmentId: 'd3e4f5a6-0003-4000-8003-300000000003',
    vendorShipmentNumber: 'RS8003',
    linkedPurchaseOrderId: 'g1a2b3c4-0021-4000-8021-210000000003',
    linkedPurchaseOrderNumber: 'PO-6003',
  },
  {
    id: 'e4f5a6b7-0004-4000-8004-400000000004',
    orderNumber: 'REP5004',
    vendorName: 'Vantage Technology Supply',
    stockLocation: 'Central Warehouse — Dallas',
    partNumber: 'SSD-3840-NVMe',
    partDescription: '3.84TB NVMe U.2 SSD',
    lineItems: [
      { partNumber: 'SSD-3840-NVMe',  partDescription: '3.84TB NVMe U.2 SSD',       quantity: 4,  unitCost: 1250.00 },
      { partNumber: 'SSD-960-NVMe',   partDescription: '960GB NVMe M.2 SSD',         quantity: 10, unitCost: 189.99 },
      { partNumber: 'RAM-32GB-DDR5',  partDescription: '32GB DDR5 ECC DIMM',         quantity: 8,  unitCost: 310.00 },
    ],
    notes: 'Priority restock for Q2 server refresh project.',
    status: 'Active',
    createdDate: '2026-03-22',
    vendorShipmentId: 'd3e4f5a6-0003-4000-8003-300000000005',
    vendorShipmentNumber: 'RS8005',
    linkedPurchaseOrderId: 'g1a2b3c4-0021-4000-8021-210000000004',
    linkedPurchaseOrderNumber: 'PO-6004',
  },
  {
    id: 'e4f5a6b7-0004-4000-8004-400000000005',
    orderNumber: 'REP5005',
    vendorName: 'Pinnacle Technology Group',
    stockLocation: 'FSL Atlanta',
    partNumber: 'PWR-1100-DC',
    partDescription: 'DC Power Supply 1100W',
    lineItems: [
      { partNumber: 'PWR-1100-DC',    partDescription: 'DC Power Supply 1100W',      quantity: 6,  unitCost: 420.00 },
      { partNumber: 'PWR-750-AC',     partDescription: 'AC Power Supply 750W',       quantity: 6,  unitCost: null   },
    ],
    status: 'History',
    createdDate: '2026-03-05',
    vendorShipmentId: 'd3e4f5a6-0003-4000-8003-300000000004',
    vendorShipmentNumber: 'RS8004',
    linkedPurchaseOrderId: 'g1a2b3c4-0021-4000-8021-210000000005',
    linkedPurchaseOrderNumber: 'PO-6005',
  },
];
